/**
 * Phase 2 — Pre-Payroll Validation widget for the PayrollRun page.
 *
 * Shows admins what HR Bench inputs will flow into the run BEFORE they
 * click Process, and BLOCKS the Process button if any entry is stuck in
 * payroll_review. Drop into PayrollRun.js right above the action bar.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Loader2, ShieldAlert, ShieldCheck, ArrowRight, Check, X, Clock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CAT_LABEL = {
  new_joiner: 'New Joiner',
  resignation: 'Resignation',
  salary_hold: 'Salary Hold',
  hold_release: 'Hold Release',
  salary_revision: 'Salary Revision',
  increment: 'Increment',
  designation_change: 'Designation',
  department_change: 'Department',
  location_change: 'Location',
  incentive: 'Incentive', bonus: 'Bonus', arrear: 'Arrear', recovery: 'Recovery',
  salary_deduction: 'Deduction', advance_deduction: 'Advance', loan_deduction: 'Loan',
  attendance_lop: 'LOP', one_time_allowance: 'One-time Allow.',
  other_monthly_input: 'Other',
};

export default function PrePayrollValidation({ companyId, periodYear, periodMonth, runStatus, onBlocked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);


  const load = useCallback(async () => {
    if (!companyId || !periodYear || !periodMonth) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/hr-bench/pre-payroll-validation?company_id=${companyId}&period_year=${periodYear}&period_month=${periodMonth}`,
        { withCredentials: true },
      );
      setData(r.data);
      if (onBlocked) onBlocked(r.data.blocking_count > 0);
    } catch (e) { /* silent — widget hides on error */ }
    setLoading(false);
  }, [companyId, periodYear, periodMonth, onBlocked]);

  useEffect(() => { load(); }, [load]);

  const bulkAction = async (action, entry_ids, remarks = '') => {
    if (entry_ids.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/hr-bench/bulk-action`,
        { entry_ids, action, remarks }, { withCredentials: true });
      const { succeeded = 0, failed = 0 } = r.data;
      if (failed > 0) toast.warning(`${succeeded} succeeded, ${failed} failed`);
      else toast.success(`${succeeded} entries ${action === 'reject' ? 'rejected' : 'accepted'}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBulkBusy(false);
  };

  if (loading) {
    return (
      <Card className="p-4" data-testid="pre-payroll-validation">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading HR Bench validation...
        </div>
      </Card>
    );
  }

  if (!data || (data.blocking_count === 0 && data.flowing_in_count === 0 && data.held_employee_ids.length === 0)) {
    return (
      <Card className="p-4 bg-emerald-50 border-emerald-200" data-testid="pre-payroll-validation">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <strong>HR Bench clear.</strong> No pending inputs for this period. Safe to process.
          </div>
        </div>
      </Card>
    );
  }

  const blocked = data.blocking_count > 0;

  return (
    <Card className={`p-4 ${blocked ? 'bg-rose-50 border-rose-300' : 'bg-violet-50 border-violet-200'}`} data-testid="pre-payroll-validation">
      <div className="flex items-start gap-3 mb-3">
        {blocked ? <ShieldAlert className="w-5 h-5 text-rose-700 mt-0.5" />
                 : <ShieldCheck className="w-5 h-5 text-violet-700 mt-0.5" />}
        <div className="flex-1">
          <div className={`text-sm font-semibold ${blocked ? 'text-rose-900' : 'text-violet-900'}`}>
            HR Bench Pre-Payroll Validation — {data.period}
          </div>
          <p className={`text-xs mt-1 ${blocked ? 'text-rose-800' : 'text-violet-800'}`}>
            {blocked
              ? <><strong>Process is BLOCKED</strong> — {data.blocking_count} HR Bench {data.blocking_count === 1 ? 'entry is' : 'entries are'} stuck in <code className="bg-white px-1 rounded">payroll_review</code>. Accept or reject them below first.</>
              : <>{data.flowing_in_count} HR Bench {data.flowing_in_count === 1 ? 'entry' : 'entries'} will auto-flow into this run. {data.held_employee_ids.length > 0 && <span className="font-semibold">{data.held_employee_ids.length} employee{data.held_employee_ids.length === 1 ? '' : 's'} on salary hold — excluded from bank file.</span>}</>
            }
          </p>
        </div>
        <Link to="/hr-bench" target="_blank">
          <Button size="sm" variant="outline" className="text-xs" data-testid="open-hr-bench-link">
            Open HR Bench <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </div>

      {blocked && (
        <div className="mt-3 border-t border-rose-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-rose-900">⚠ Blocking entries ({data.blocking_count})</span>
            <div className="flex gap-1">
              <Button
                size="sm"
                disabled={bulkBusy}
                onClick={() => bulkAction('payroll_accept', data.blocking.map(e => e.id))}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
                data-testid="bulk-accept-blocking-btn"
              >
                {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                Accept All
              </Button>
              <Button
                size="sm" variant="outline"
                disabled={bulkBusy}
                onClick={() => {
                  const reason = prompt('Rejection reason for ALL blocking entries:');
                  if (reason && reason.trim()) bulkAction('reject', data.blocking.map(e => e.id), reason.trim());
                }}
                className="text-rose-700 text-xs h-7"
                data-testid="bulk-reject-blocking-btn"
              >
                <X className="w-3 h-3 mr-1" /> Reject All
              </Button>
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto bg-white border border-rose-200 rounded">
            <table className="w-full text-xs">
              <tbody>
                {data.blocking.slice(0, 20).map(e => (
                  <tr key={e.id} className="border-b border-rose-100">
                    <td className="px-2 py-1.5">
                      <span className="font-medium">{CAT_LABEL[e.category] || e.category}</span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">
                      {e._employee?.name || '—'} <span className="text-slate-400">({e._employee?.code})</span>
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-500">
                      {e.payload?.amount ? `₹${Number(e.payload.amount).toLocaleString('en-IN')}` :
                       e.payload?.new_ctc ? `CTC ₹${Number(e.payload.new_ctc).toLocaleString('en-IN')}` :
                       e.remarks || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!blocked && data.flowing_in_count > 0 && (
        <details className="mt-2 border-t border-violet-200 pt-2">
          <summary className="text-xs text-violet-800 cursor-pointer">Show {data.flowing_in_count} flowing-in entries</summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
            {data.flowing_in.slice(0, 30).map(e => (
              <div key={e.id} className="text-xs bg-white border border-violet-200 rounded px-2 py-1">
                <span className="font-medium text-violet-700">{CAT_LABEL[e.category] || e.category}</span>
                <span className="text-slate-600 ml-1">• {e._employee?.code || '—'}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}
